import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useAddCompany,
  useGetBusinessSectors,
  useGetCollectiveAgreements,
} from '../../../hooks/useCompany';
import {
  Alert,
  Input,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TYPE_MESSAGE,
  SelectTriggerAsChild,
  UploadImage,
  Button,
  SpinnerIcon,
  APP_REFERER,
  handleAppError,
} from '@jaam/web-core';
import { useToast } from '../../../context/Toast/ToastProvider';
import OHADA_COUNTRIES from '../../../constants/ohada-countries.constants';
import { CompanyDto } from '@jaam/commons/dtos';

interface IProps {
  firstCreation: boolean;
  cancel: () => void;
}

const REQUIRED_LABEL_SUFFIX = ` ${'\u002A'}`;

const URL_APP_REFERER: Record<APP_REFERER, string | undefined> = {
  accounts: process.env.NX_ACCOUNTS_PUBLIC_URL,
  payrollr: process.env.NX_PAYROLLR_PUBLIC_URL,
  timesheets: process.env.NX_TIMESHEETS_PUBLIC_URL,
  edeclaration: process.env.NX_EDECLARATION_PUBLIC_URL,
};

// Module-level schema shape for type inference
const _schemaShape = z.object({
  name: z.string(),
  country: z.string(),
  numNui: z.string(),
  commercialRegister: z.string(),
  collectiveAgreementId: z.number().nonnegative().min(1),
  businessSectorId: z.number().optional(),
  logo: z.string().optional(),
  numCnps: z.string().optional(),
});
type FormValues = z.infer<typeof _schemaShape>;

const CreateCompany = ({ firstCreation, cancel }: IProps): JSX.Element => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAlertErrorVisible, setIsAlertErrorVisible] = useState(false);

  const redirectToReferer = useCallback((): void => {
    let urlAppReferer = process.env.NX_PAYROLLR_PUBLIC_URL || '/';
    const appReferer = localStorage.getItem('appReferer') as APP_REFERER | null;

    if (appReferer && URL_APP_REFERER[appReferer]) {
      urlAppReferer = URL_APP_REFERER[appReferer] || 'payrollr';
    }

    if (urlAppReferer) {
      window.location.href = urlAppReferer;
      localStorage.removeItem('appReferer');
    }
  }, []);

  const schema = useMemo(
    () =>
      _schemaShape.extend({
        name: z.string().nonempty(t('company.errors.name.required')),
        country: z.string().nonempty(t('company.errors.country.required')),
        numNui: z.string().nonempty(t('company.errors.numNui.required')),
        commercialRegister: z
          .string()
          .nonempty(t('company.errors.commercialRegister.required')),
        collectiveAgreementId: z
          .number()
          .nonnegative(t('company.errors.collectiveAgreementId.required'))
          .min(1, t('company.errors.collectiveAgreementId.invalid')),
      }),
    [t],
  );

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      country: 'CMR',
      numNui: '',
      commercialRegister: '',
      collectiveAgreementId: 0,
      businessSectorId: undefined,
      logo: '',
      numCnps: '',
    },
  });

  const handleAddSuccess = useCallback(() => redirectToReferer(), [redirectToReferer]);

  const handleAddError = useCallback(() => {
    setIsAlertErrorVisible(true);
  }, []);

  const { addCompany, isAddingCompany, addCompanyError } = useAddCompany({
    onSuccess: () => {
      toast.open(t('company.created'));
      handleAddSuccess();
    },
    onError: (error) => {
      handleAppError(toast, error, t('errors.default'));
      handleAddError();
    },
    setError,
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const payload: CompanyDto = {
          ...values,
          businessSectorId: values.businessSectorId ?? undefined,
          numCnps: values.numCnps?.trim() ? values.numCnps.trim() : undefined,
        };
        addCompany(payload);
      } catch (error) {
        // To avoid app to crash, the error will be set on the form
      }
    },
    [addCompany],
  );

  const handleImageChange = useCallback(
    (_field: string, value: string) => {
      setValue('logo', value);
      clearErrors('logo');
    },
    [setValue, clearErrors],
  );

  const handleImageError = useCallback(
    (message: string) => {
      setError('logo', { message });
    },
    [setError],
  );

  const handleCountryChange = useCallback(
    (value: string) => setValue('country', value),
    [setValue],
  );

  const handleAlertDismiss = useCallback(() => setIsAlertErrorVisible(false), []);

  const countryItems = useMemo(
    () =>
      OHADA_COUNTRIES.map((country) => (
        <SelectItem key={country.value} value={country.value}>
          <div className="flex items-center gap-2">
            {country.icon && <span className="h-6">{country.icon}</span>}
            <span>{t(country.text)}</span>
          </div>
        </SelectItem>
      )),
    [t],
  );

  const {
    collectiveAgreements,
    isFetchingCollectiveAgreements,
    agreementsError: isFetchingCollectiveAgreementsError,
  } = useGetCollectiveAgreements({});

  const {
    businessSectors,
    isFetchingBusinessSectors,
    sectorsError: isFetchingBusinessSectorsError,
  } = useGetBusinessSectors({});

  const collectiveAgreementItems = useMemo(
    () =>
      (collectiveAgreements ?? [])
        .filter(
          (agreement) =>
            agreement.value !== null && agreement.value !== undefined,
        )
        .map((agreement) => (
          <SelectItem
            key={agreement.value}
            value={(agreement.value as number | string).toString()}
          >
            {agreement.label}
          </SelectItem>
        )),
    [collectiveAgreements],
  );

  const businessSectorItems = useMemo(
    () =>
      (businessSectors ?? []).map((businessSector) => (
        <SelectItem
          key={businessSector.value}
          value={businessSector.value.toString()}
        >
          {businessSector.label}
        </SelectItem>
      )),
    [businessSectors],
  );

  return (
    <div
      className={`${firstCreation ? 'p-7 m-4 rounded-3xl shadow-lg mt-10' : ''
        } max-w-[524px] flex flex-col md:min-w-[524px] gap-6 bg-white md:mx-auto`}
    >
      <h1 className="mb-4 text-2xl font-bold text-center">
        {firstCreation ? t('company.title') : t('company.title')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <fieldset disabled={isAddingCompany} className="flex flex-col gap-4">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={t('company.fields.name') + REQUIRED_LABEL_SUFFIX}
                error={errors.name?.message}
                disabled={isAddingCompany}
              />
            )}
          />
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <SelectRoot
                onValueChange={handleCountryChange}
                value={field.value}
              >
                <SelectTrigger asChild>
                  <SelectTriggerAsChild
                    label={t('company.fields.country') + REQUIRED_LABEL_SUFFIX}
                    value={field.value}
                  >
                    <SelectValue />
                  </SelectTriggerAsChild>
                </SelectTrigger>
                <SelectContent>{countryItems}</SelectContent>
              </SelectRoot>
            )}
          />
          <Controller
            name="collectiveAgreementId"
            control={control}
            rules={{ required: t('company.errors.collectiveAgreementId.required') }}
            render={({ field, fieldState }) => (
              <SelectRoot
                onValueChange={(value) => field.onChange(Number(value))}
                value={field.value?.toString() || ''}
              >
                <SelectTrigger asChild>
                  <SelectTriggerAsChild
                    label={t('company.fields.collectiveAgreement') + REQUIRED_LABEL_SUFFIX}
                    value={field.value?.toString()}
                    error={fieldState.error?.message}
                  >
                    <SelectValue />
                  </SelectTriggerAsChild>
                </SelectTrigger>
                <SelectContent>
                  {isFetchingCollectiveAgreements ? (
                    <div className="flex items-center justify-center h-10 p-4 text-center">
                      {t('loading')}
                    </div>
                  ) : isFetchingCollectiveAgreementsError ? (
                    <div className="flex items-center justify-center h-10 text-center">
                      {t('error.noOptions')}
                    </div>
                  ) : (
                    collectiveAgreementItems
                  )}
                </SelectContent>
              </SelectRoot>
            )}
          />
          <Controller
            name="businessSectorId"
            control={control}
            render={({ field, fieldState }) => (
              <SelectRoot
                onValueChange={(value) =>
                  field.onChange(
                    value === '__none__' ? undefined : Number(value),
                  )
                }
                value={
                  field.value != null ? field.value.toString() : '__none__'
                }
              >
                <SelectTrigger asChild>
                  <SelectTriggerAsChild
                    label={t('company.fields.businessSector')}
                    value={
                      field.value != null
                        ? (businessSectors ?? [])?.find(
                            (sector) =>
                              sector.value?.toString() ===
                              field.value?.toString(),
                          )?.label ?? ''
                        : '—'
                    }
                    error={fieldState.error?.message}
                  >
                    {field.value != null
                      ? (businessSectors ?? [])?.find(
                          (sector) =>
                            sector.value?.toString() ===
                            field.value?.toString(),
                        )?.label ?? ''
                      : '—'}
                  </SelectTriggerAsChild>
                </SelectTrigger>
                <SelectContent>
                  {isFetchingBusinessSectors ? (
                    <div className="flex items-center justify-center h-10 p-4 text-center">
                      {t('loading')}
                    </div>
                  ) : isFetchingBusinessSectorsError ? (
                    <div className="flex items-center justify-center h-10 text-center ">
                      {t('error.noOptions')}
                    </div>
                  ) : (
                    <>
                      <SelectItem value="__none__">—</SelectItem>
                      {businessSectorItems}
                    </>
                  )}
                </SelectContent>
              </SelectRoot>
            )}
          />
          <Controller
            name="numNui"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={t('company.fields.taxNumber') + REQUIRED_LABEL_SUFFIX}
                error={errors.numNui?.message}
              />
            )}
          />
          <Controller
            name="commercialRegister"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={t('company.fields.commercialRegister') + REQUIRED_LABEL_SUFFIX}
                error={errors.commercialRegister?.message}
                disabled={isAddingCompany}
              />
            )}
          />
          <Controller
            name="numCnps"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={t('company.fields.numCnps', { defaultValue: 'N° CNPS' })}
                error={errors.numCnps?.message}
                disabled={isAddingCompany}
              />
            )}
          />
          <UploadImage
            name="logo"
            error={errors.logo?.message}
            showError={!!errors.logo}
            changeImage={handleImageChange}
            setErrorFromImage={handleImageError}
            disabled={isAddingCompany}
          />
          {addCompanyError && isAlertErrorVisible &&
            addCompanyError.details?.length > 0 &&
            addCompanyError.details.map((detail) => (
              <Alert
                typeMessage={TYPE_MESSAGE.ERROR}
                show
                handle={handleAlertDismiss}
                texte={detail.translatedMessage || t('errors.default')}
              />
            ))}
        </fieldset>
        <Button
          type="submit"
          className="w-full gap-2"
          disabled={isAddingCompany}
        >
          {isAddingCompany && <SpinnerIcon />}
          {t('company.buttons.create')}
        </Button>
        <Button
          disabled={isAddingCompany}
          className="w-full"
          variant="secondary"
          onClick={cancel}
        >
          {t('company.buttons.cancel')}
        </Button>
      </form>
    </div>
  );
};

export default React.memo(CreateCompany);
